import type React from 'react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createBot, listBots } from '../api/admin';
import { useInfiniteList } from '../api/useInfiniteList';
import { Avatar, Modal, Pill } from '../components/ui';
import type { BotPersonality } from '../types/admin';

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;

const PERSONALITIES: BotPersonality[] = ['balanced', 'aggressive', 'defensive', 'combo'];

export function BotsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  const fetchPage = useCallback(
    (cursor: string | null) => listBots(cursor, debounced, PAGE_SIZE),
    [debounced],
  );
  const { items, loading, error, hasMore, sentinelRef, reset } = useInfiniteList(fetchPage);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Bots</h1>
          <p className="page__subtitle">
            {items.length} carregado(s){hasMore ? '…' : ''}
          </p>
        </div>
        <div className="page__actions">
          <input
            className="search"
            placeholder="Buscar bot…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            Novo bot
          </button>
        </div>
      </header>

      {error ? <div className="banner banner--error">{error.message}</div> : null}

      <div className="data-table" role="table">
        <div className="data-table__head data-table__head--bots" role="row">
          <span role="columnheader">Bot</span>
          <span role="columnheader">Usuário</span>
          <span role="columnheader">Nível</span>
          <span role="columnheader">Estilo</span>
          <span role="columnheader">Deck</span>
        </div>

        {items.map((bot) => (
          <div
            className="data-table__row data-table__row--bots data-table__row--clickable"
            role="row"
            key={bot.id}
            onClick={() => navigate(`/bots/${bot.id}`)}
          >
            <span className="cell cell--identity" role="cell">
              <Avatar src={bot.profileImgUrl} name={bot.displayName} />
              <span className="cell__name">{bot.displayName}</span>
              {/* A bot left in "deleted" is one whose purge failed midway; it is
                  out of matchmaking but still needs the delete retried. */}
              {bot.status !== 'active' ? <Pill tone="danger">{bot.status}</Pill> : null}
            </span>
            <span className="cell cell--dim" role="cell">
              @{bot.userName}
            </span>
            <span className="cell" role="cell">
              {bot.level ?? '—'}
            </span>
            <span className="cell" role="cell">
              {bot.botPersonality ? <Pill>{bot.botPersonality}</Pill> : '—'}
            </span>
            <span className="cell cell--dim" role="cell">
              {bot.defaultDeckId ? '✓' : 'sem deck'}
            </span>
          </div>
        ))}

        {!loading && items.length === 0 ? (
          <div className="empty-state">
            {debounced ? 'Nenhum bot encontrado.' : 'Nenhum bot ainda.'}
          </div>
        ) : null}
      </div>

      {loading ? <div className="page-loading">Carregando…</div> : null}
      {hasMore ? <div ref={sentinelRef} className="scroll-sentinel" /> : null}
      {!hasMore && items.length > 0 ? <div className="list-end">Fim da lista.</div> : null}

      {creating ? (
        <CreateBotModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            reset();
            navigate(`/bots/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateBotModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}): React.JSX.Element {
  const [userName, setUserName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [level, setLevel] = useState(5);
  const [personality, setPersonality] = useState<BotPersonality>('balanced');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await createBot({
        userName: userName.trim(),
        displayName: displayName.trim(),
        level,
        botPersonality: personality,
      });
      onCreated(result.bot.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar o bot.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Novo bot" onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)}>
        <label className="field">
          <span>Nome de exibição</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={32}
            autoFocus
            required
          />
        </label>
        <label className="field">
          <span>Nome de usuário (único)</span>
          <input
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder="bot_exemplo"
            required
          />
        </label>
        <div className="field-row">
          <label className="field">
            <span>Nível (1–100)</span>
            <input
              type="number"
              min={1}
              max={100}
              value={level}
              onChange={(e) => setLevel(Number(e.target.value))}
              required
            />
          </label>
          <label className="field">
            <span>Estilo de jogo</span>
            <select
              value={personality}
              onChange={(e) => setPersonality(e.target.value as BotPersonality)}
            >
              {PERSONALITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="field__hint">
          O bot recebe automaticamente o inventário inicial e um deck de 12 cartas.
        </p>

        {error ? <div className="form-error">{error}</div> : null}

        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? 'Criando…' : 'Criar bot'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
